/**
 * One row of the root `/tasks` page (`MCK-20260521-009`): what it is, what it is called, which
 * project it belongs to, where it stands, when it is due and when it was raised.
 *
 * **The register's row with one column swapped.** The assignee is gone — every row on this page
 * is the reader's own — and the project is in its place, because a list spanning every project
 * is unreadable without it. The project name arrives already resolved: it is the page's join
 * against the project list B1 already holds, never a request per row.
 *
 * **"Raised", not "updated".** The page is ordered by `created_at` (the server's keyset), so the
 * date a row shows is the one its position means; `updated_at` is on the wire and unused here.
 *
 * **The studio chip.** A person employed by two studios reads one list over both, and a row
 * from the studio this tab is *not* acting as says so (`MCK-20260521-009`, the inbox's rule
 * applied to the page that replaced it on the sign-in path). The page decides whether a chip
 * is due — it holds the companies and the active workspace — and the row only draws the name.
 */

import { Link } from "@tanstack/react-router";

import { Pill } from "@/components";
import { relativeTime } from "@/lib/i18n";
import type { Translate } from "@/lib/i18n";

import type { Task } from "./api";
import styles from "./MyTasks.module.css";
import { KindGlyph, STATUS_TONE, STATUS_KEY } from "./TaskRegister";
import { KIND_KEY } from "./kinds";
import taskStyles from "./Tasks.module.css";

export interface MyTaskRowProps {
  readonly task: Task;
  readonly projectName: string;
  /** The studio's name when the row belongs to a company other than the active one. */
  readonly studio: string | null;
  /**
   * Who owes it — **only while the page is about somebody other than the reader** (owner,
   * 2026-09-16). Absent on a person's own list, where every row is theirs and a column
   * repeating their name on every line says nothing.
   */
  readonly who?: string;
  readonly t: Translate<"tasks">;
  readonly locale: string;
}

export function MyTaskRow({ task, projectName, studio, who, t, locale }: MyTaskRowProps) {
  return (
    <article className={[styles.row, who === undefined ? "" : styles.rowWithWho]
      .filter(Boolean)
      .join(" ")}
    >
      <KindGlyph kind={task.kind} label={t(KIND_KEY[task.kind])} taskTypeId={task.task_type_id} />
      <p className={styles.title}>
        {/* The row's one navigation, to C8 — which already exists and already knows how to
            refuse a task this person may no longer see. */}
        <Link
          to="/projects/$projectId/tasks/$taskId"
          params={{ projectId: task.project_id, taskId: task.id }}
          className={taskStyles.rowLink}
        >
          <span className={taskStyles.number}>{t("row.number", { number: task.number })}</span>{" "}
          {task.title}
        </Link>
      </p>
      {who === undefined ? null : <p className={styles.cell}>{who}</p>}
      <p className={styles.cell}>
        {projectName}
        {studio === null ? null : (
          <>
            {" "}
            <Pill tone="neutral">{studio}</Pill>
          </>
        )}
      </p>
      {/* The wrapper carries the grid placement; the pill is the one badge (`STYLE.md` §10)
          and takes no class of its own. */}
      <span className={styles.status}>
        <Pill tone={STATUS_TONE[task.status]}>{t(STATUS_KEY[task.status])}</Pill>
      </span>
      <p className={[styles.cell, styles.wide].join(" ")}>
        {task.due_at === null ? t("row.noDue") : relativeTime(task.due_at, locale)}
      </p>
      <p className={[styles.cell, styles.wide].join(" ")}>
        {relativeTime(task.created_at, locale)}
      </p>
    </article>
  );
}
